<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { QSlider } from 'quasar';
import { getLabel, getLocale } from '@/utils/data-display-utils';

import { getTranslation } from '@/utils/data-display-utils';
import { Locales } from '@/store/language.interface';

// PROPS
const props = defineProps<{
  levels: any;
  choices: any;
  statistiques?: any;
  results?: any;
  voteSelected: any;
  languageEnabled?: Locales[];
  sectionProhibitsResultsAndComments?: boolean;
}>();

const range = props.choices.reduce(
  (acc: any[], item: { value: number }) => {
    if (item.value < acc[0]) acc[0] = item.value;
    if (item.value > acc[1]) acc[1] = item.value;
    return acc;
  },
  [Infinity, -Infinity],
);

// EMITS
const emit = defineEmits<{
  (e: 'change', rateValue: number): void;
}>();

// CONST
const rateValue = ref<number>(
  props.results?.find((result) => result.answer_id === props.levels.answerId) != null
    ? props.results.find((result) => result.answer_id === props.levels.answerId).value_id[0]
    : range[0],
);
const isActive = ref<boolean>(false);
const locale = computed(() => getLocale(props.languageEnabled));

// LIFECYCLE
onMounted(() => {
  if (rateValue.value != range[0]) {
    isActive.value = true;
  }
});

// FUNCTIONS
function changeValue(value: any) {
  emit('change', value);
}

function resetValue() {
  rateValue.value = 1;
  isActive.value = false;
}

function updateColor() {
  isActive.value = true;
}

// WATCH
watch(rateValue, (newValue) => {
  if (newValue != 1) {
    changeValue({ valueId: newValue, answerId: props.levels.answerId });
  } else if (newValue === 1 && isActive.value) {
    changeValue({ valueId: newValue, answerId: props.levels.answerId });
  }
});

watch(
  () => props.voteSelected.length,
  (newLength) => {
    if (newLength === 0) {
      resetValue();
    }
  },
);

// COMPUTED
const selectedLabel = computed(() => {
  if (isActive.value && rateValue.value != null) {
    const selectedValue = props.choices.find((r) => r.value === rateValue.value);
    return selectedValue.label[locale.value];
  } else {
    return getLabel('form.select-value', props.languageEnabled);
  }
});

const endLabels = computed(() => {
  const labels = {};
  const length = props.choices.length;

  if (props.levels != undefined) {
    const firstValue = props.choices.find((r) => r.value === 1);
    labels[range[0]] = getTranslation(firstValue, 'label', props.languageEnabled);
    const endValue = props.choices.find((r) => r.value === length);
    labels[range[1]] = getTranslation(endValue, 'label', props.languageEnabled);
    return labels;
  }
});

const labelColor = computed(() => {
  if (isActive.value) {
    return 'secondary';
  } else {
    return 'white';
  }
});

const labelTextColor = computed(() => {
  if (isActive.value) {
    return 'white';
  } else {
    return 'black';
  }
});

const thumbColor = computed(() => {
  if (isActive.value) {
    return 'secondary';
  } else {
    return 'grey';
  }
});

watch(selectedLabel, (newValue, oldValue) => {
  if (
    oldValue === getLabel('form.select-value', props.languageEnabled) &&
    !(newValue === getLabel('form.select-value', props.languageEnabled))
  ) {
    changeValue({ valueId: rateValue.value, answerId: props.levels.answerId });
  }
});
</script>
<template>
  <div class="question-rate-wrapper">
    <div class="question-rate">
      <div class="question-rate__title">
        {{ getTranslation(props.levels, 'name', props.languageEnabled) }}
      </div>
      <div class="question-rate__content">
        <q-slider
          v-model="rateValue"
          :id="props.levels.id"
          markers
          :marker-labels="endLabels"
          label-always
          :label-value="selectedLabel"
          :min="range[0]"
          :max="range[1]"
          thumb-size="20px"
          :thumb-color="thumbColor"
          :label-color="labelColor"
          :label-text-color="labelTextColor"
          inner-track-color="grey"
          track-color="grey"
          selection-color="grey"
          @change="updateColor"
        >
        </q-slider>
      </div>
    </div>
  </div>
</template>
<style lang="scss">
.question-rate {
  background-color: $color-neutral-grey-300;
  padding: $space-md $space-md;
  border-radius: 8px;
  margin: $space-sm 0;

  &__title {
    font-weight: bold;
    font-size: $small-title;
    margin: 0 0 $space-xs 0;
  }

  &__content {
    padding: $space-lg $space-md $space-md $space-md;
  }

  .q-slider {
    &__track-container {
      width: auto;
    }

    &__marker-labels {
      width: 40%;
      position: relative;
      overflow-wrap: break-word;

      &-container {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
      }

      &:nth-child(2) {
        left: unset !important;
        text-align: end;
      }

      &--h-ltr {
        transform: none !important;
      }
    }

    &.active {
      background-color: aqua !important;
    }

    &__label {
      min-width: 180px !important;
      max-width: 180px;
      overflow-wrap: break-word;
      text-wrap: auto;
    }
  }
}
</style>
